// libs
import React, { memo, useEffect, useState } from "react";
import Helmet from "react-helmet";

// global components
import WithStyles from "shared/components/WithStyles";
import Modal, { ModalContext } from "shared/components/Modal";

// api
import { updateCookieConsent } from "shared/api/gdpr";

// helpers
import { getCookie, setCookie } from "shared/helpers/utils";

// constants
import { LINKS } from "shared/components/GDPR/constants";
import { TEXT, POP_UP, CONSENT, POP_UP_COOKIE_KEY } from "./constants";

// styles
import { acceptBtnCss, cookiesContentCss, modalCss } from "./styles";

// defs
import { ICookiesContentProps } from "./types";

function CookiesConsent({
    onGDPRNetwork,
    openCountryCurrencySettings,
    allowXHRRequests,
    isXHRAllowed,
}: ICookiesContentProps) {
    const [showPopup, setShowPopup] = useState(false);

    useEffect(() => {
        let cookieTimer: NodeJS.Timeout;
        const actionAfter5Sec = () => {
            if (getCookie(POP_UP_COOKIE_KEY) !== POP_UP.HIDE) {
                updateCookieConsent(CONSENT.EMPTY).then(() => {
                    setCookie(POP_UP_COOKIE_KEY, POP_UP.SHOW, 1);
                    allowXHRRequests();
                });
            }
        };

        const show_pop_cookie = getCookie(POP_UP_COOKIE_KEY);

        switch (show_pop_cookie) {
            case POP_UP.SHOW: {
                setShowPopup(true);
                allowXHRRequests();
                break;
            }

            case POP_UP.HIDE: {
                setShowPopup(false);
                allowXHRRequests();
                break;
            }

            default: {
                cookieTimer = setTimeout(() => {
                    actionAfter5Sec();
                }, 5000);
                setShowPopup(true);
            }
        }

        return () => {
            if (cookieTimer !== undefined) {
                window.clearTimeout(cookieTimer);
            }
        };
    }, [allowXHRRequests]);

    if (onGDPRNetwork === false) {
        return null;
    }

    const onModalHide = () => {
        setShowPopup(false);
    };

    return (
        <>
            {isXHRAllowed === false && (
                <Helmet
                    script={[
                        {
                            innerHTML: `
                            var cookieDesc = Object.getOwnPropertyDescriptor(Document.prototype, 'cookie') ||
                            Object.getOwnPropertyDescriptor(HTMLDocument.prototype, 'cookie');
                                if (cookieDesc && cookieDesc.configurable ) {
                                    Object.defineProperty(document, 'cookie', {
                                        get: function () {
                                            return cookieDesc.get.call(document);
                                        },
                                        set: function (val) {
                                            var ca = decodeURIComponent(document.cookie);
                                            if(ca.indexOf("show_pop=") > -1 || val.indexOf("show_pop") > -1){
                                                cookieDesc.set.call(document, val);
                                            }
                                            
                                        }
                                    });
                                }
                        `,
                        },
                    ]}
                />
            )}

            {showPopup && (
                <Modal
                    css={modalCss}
                    className="modal"
                    showCrossIcon={false}
                    allowBackdropClick={false}
                    onHide={onModalHide}
                >
                    <ModalContext.Consumer>
                        {({ hide }) => {
                            const hideModal = () => {
                                updateCookieConsent(CONSENT.YES).then(() => {
                                    setCookie(POP_UP_COOKIE_KEY, POP_UP.HIDE, 365);
                                    openCountryCurrencySettings();
                                    allowXHRRequests();
                                    hide();
                                });
                            };

                            return (
                                <>
                                    <WithStyles
                                        cssProps={cookiesContentCss}
                                        data-test="cookie-popup"
                                    >
                                        <div className="cookies-title">{TEXT.TITLE}</div>
                                        <div
                                            className="cookie-content"
                                            dangerouslySetInnerHTML={{
                                                __html: TEXT.COOKIE_CONSENT_INFO(
                                                    LINKS.PRIVACY_POLICY
                                                ),
                                            }}
                                        />
                                    </WithStyles>

                                    <WithStyles
                                        as="button"
                                        cssProps={acceptBtnCss}
                                        onClick={hideModal}
                                        data-test="cookie-accept"
                                    >
                                        {TEXT.ACCEPT_LABEL}
                                    </WithStyles>
                                </>
                            );
                        }}
                    </ModalContext.Consumer>
                </Modal>
            )}
        </>
    );
}

export default memo(CookiesConsent);
